/**
 * One function per path of adminwebapi.
 *
 * The screens never build a path or a query themselves: everything the application can ask
 * for is listed here, and it lines up one to one with the route table of the api.
 */

import { api } from "./client";
import type {
  BlockedAuthorResult,
  BlockedChatPreview,
  GrantResult,
  Me,
  PaymentsPage,
  RefundPreparation,
  RefundRejectResult,
  RefundResult,
  ScraperChat,
  StatsPeriod,
  TicketReplyResult,
  TicketThread,
  TicketsCount,
  TicketsCursor,
  TicketsPage,
  UsersStats,
} from "./types";

export type TicketsFilter = "open" | "closed" | "any";

export function getMe(): Promise<Me> {
  return api.get<Me>("/me");
}

export function getTickets(
  status: TicketsFilter,
  cursor: TicketsCursor | null,
  limit: number,
  userId?: number,
  signal?: AbortSignal,
): Promise<TicketsPage> {
  return api.get<TicketsPage>(
    "/tickets",
    {
      status,
      limit,
      user_id: userId,
      // both halves or neither: the api refuses half a cursor, and it is right to
      before_at: cursor?.before_at,
      before_id: cursor?.before_id,
    },
    signal,
  );
}

export function getTicketsCount(status: TicketsFilter = "open"): Promise<TicketsCount> {
  return api.get<TicketsCount>("/tickets/count", { status });
}

export function getTicket(ticketId: number, signal?: AbortSignal): Promise<TicketThread> {
  return api.get<TicketThread>("/ticket", { ticket_id: ticketId }, signal);
}

/**
 * Answer a client.
 *
 * `request_id` is one attempt of this application, not one message: a retry of the same
 * attempt reuses it and the api sends nothing the second time, while a new attempt after a
 * failure gets a new one and is a genuinely new answer.
 *
 * No `admin_id` here - adminwebapi writes the verified one into the body, and anything sent
 * under that name would be replaced anyway.
 */
export function replyToTicket(
  ticketId: number,
  text: string,
  requestId: string,
  close: boolean,
): Promise<TicketReplyResult> {
  return api.post<TicketReplyResult>("/ticket/reply", {
    ticket_id: ticketId,
    text,
    request_id: requestId,
    close,
  });
}

export function closeTicket(ticketId: number, notifyClient = true): Promise<TicketThread> {
  return api.put<TicketThread>("/ticket/close", { ticket_id: ticketId, notify_client: notifyClient });
}

export function getRefundRequests(limit = 20): Promise<PaymentsPage> {
  return api.get<PaymentsPage>("/refunds/requests", { limit });
}

export function getPayments(userId: number, limit = 20): Promise<PaymentsPage> {
  return api.get<PaymentsPage>("/payments", { user_id: userId, limit });
}

export function prepareRefund(paymentId: number): Promise<RefundPreparation> {
  return api.put<RefundPreparation>("/refunds/prepare", { payment_id: paymentId });
}

export function cancelRefund(paymentId: number): Promise<{ status: number }> {
  return api.put<{ status: number }>("/refunds/cancel", { payment_id: paymentId });
}

export function confirmRefund(paymentId: number): Promise<RefundResult> {
  return api.post<RefundResult>("/refunds/confirm", { payment_id: paymentId });
}

/**
 * Turn the client's refund request down and tell them about it.
 *
 * One call and not two: recording the rejection and telling the client belong together, and
 * the client told is whoever owns the payment - adminwebapi takes them from clientbackapi's
 * answer, never from here.
 */
export function rejectRefund(paymentId: number): Promise<RefundRejectResult> {
  return api.post<RefundRejectResult>("/refunds/reject", { payment_id: paymentId });
}

/**
 * Credit a client's balance.
 *
 * `tokens` is whole tokens, not cents: the conversion is the api's, and so are the ceiling and
 * the ledger key. `requestId` is one attempt of this application, not one grant - a retry of
 * the same attempt credits nothing the second time, while a deliberate second grant to the
 * same client is a new attempt and gets a new one.
 *
 * No `admin_id` here - adminwebapi writes the verified one into the record, and anything sent
 * under that name would be ignored anyway.
 */
export function grantTokens(
  userId: number,
  tokens: number,
  requestId: string,
  comment?: string,
): Promise<GrantResult> {
  return api.post<GrantResult>("/grants/tokens", {
    user_id: userId,
    tokens,
    request_id: requestId,
    comment: comment && comment.trim() ? comment.trim() : undefined,
  });
}

export function resolveChat(row: string, signal?: AbortSignal): Promise<ScraperChat> {
  return api.get<ScraperChat>("/chats/resolve", { row }, signal);
}

export function addScraperChat(row: string): Promise<ScraperChat> {
  return api.post<ScraperChat>("/scrapers/chats", { row });
}

/**
 * What blocking this chat would cost. Reads only and changes nothing, which is why it is a
 * plain table route while blocking itself is a composite one.
 */
export function getBlockedChatPreview(chatId: number, signal?: AbortSignal): Promise<{ preview: BlockedChatPreview }> {
  return api.get<{ preview: BlockedChatPreview }>("/chats/blocked", { chat_id: chatId }, signal);
}

/**
 * Take a chat off monitoring, delete its stored history and leave it.
 *
 * One call and not three: the order of those steps decides what a half finished attempt costs,
 * and a browser must not own it. Telling the clients is not part of it either - that is a
 * fan-out the pipeline does within minutes.
 */
export function blockChat(chatId: number, reason: string): Promise<{ status: number; message?: string }> {
  return api.post<{ status: number; message?: string }>("/chats/block", { chat_id: chatId, reason });
}

/**
 * Put a chat back on monitoring. Answers 404 when it was not taken off it.
 *
 * A plain call and not the mirror of blockChat: it undoes the first of that call's three steps
 * and none of the others. The deleted history does not come back and the scraper does not walk
 * back into the chat, which the screen says after every success.
 */
export function unblockChat(chatId: number): Promise<{ status: number; message?: string }> {
  return api.delete<{ status: number; message?: string }>("/chats/blocked", undefined, { chat_id: chatId });
}

/**
 * Put a message author who objected on the list of excluded authors.
 *
 * Only the list: the pipeline does not read it yet, which the screen says right after adding.
 *
 * The id goes in the body and never in the query: adminwebapi logs every request line, and the
 * notice for chat participants promises that this id is kept nowhere but as a digest.
 */
export function blockAuthor(authorId: number): Promise<BlockedAuthorResult> {
  return api.post<BlockedAuthorResult>("/authors/blocked", { author_id: authorId });
}

/** Take a message author off the list. Answers 404 when they were not on it. */
export function unblockAuthor(authorId: number): Promise<{ status: number; message?: string }> {
  return api.delete<{ status: number; message?: string }>("/authors/blocked", { author_id: authorId });
}

/** New and active clients over a rolling window, one bucket per hour, day or month. */
export function getUsersStats(period: StatsPeriod, signal?: AbortSignal): Promise<UsersStats> {
  return api.get<UsersStats>("/stats/users", { period }, signal);
}
